/** ワークスペースホームのタブ。ボード slug と衝突しない予約パス。 */
export const WORKSPACE_HOME_SECTIONS = ['dashboard', 'pages', 'threads'] as const;

export type WorkspaceHomeSection = (typeof WORKSPACE_HOME_SECTIONS)[number];

export type WorkspaceHomeLanding = 'agent' | 'dashboard' | 'pages';

export type ParsedWorkspaceHomePath =
  | { section: 'root' }
  | { section: 'dashboard' }
  | { section: 'pages' }
  | { section: 'threads'; threadId: string | null };

export function workspaceDashboardPath(workspaceId: string): string {
  return `/${workspaceId}/dashboard`;
}

/** 1ページ。`/{workspaceId}/{slug}` だとタブ用パスと重なる。 */
export function workspacePagePath(workspaceId: string, slugOrId: string): string {
  const segment = String(slugOrId ?? '').trim();
  return `/${workspaceId}/page/${segment}`;
}

/** ページ一覧。公開 RAG 管理者のホーム着地と分けるため専用パスにする。 */
export function workspacePageListPath(workspaceId: string, options?: { embed?: boolean }): string {
  const params = new URLSearchParams();
  if (options?.embed) {
    params.set('embed', 'true');
  }
  const query = params.toString();
  return `/${workspaceId}/pages${query ? `?${query}` : ''}`;
}

export function workspaceThreadsPath(
  workspaceId: string,
  options?: { threadId?: string; search?: string },
): string {
  const threadId = options?.threadId?.trim();
  const base = threadId
    ? `/${workspaceId}/threads/${encodeURIComponent(threadId)}`
    : `/${workspaceId}/threads`;
  const search = options?.search?.replace(/^\?/, '') ?? '';
  return search ? `${base}?${search}` : base;
}

export function workspaceThreadCreatePath(
  workspaceId: string,
  options: { sourceBoardId?: string; threadTitle?: string },
): string {
  const params = new URLSearchParams();
  params.set('create', '1');
  if (options.sourceBoardId) params.set('sourceBoardId', options.sourceBoardId);
  if (options.threadTitle) params.set('threadTitle', options.threadTitle);
  return workspaceThreadsPath(workspaceId, { search: params.toString() });
}

/** ワークスペースを開いたときの着地先。エージェントは `/:workspaceId` のまま。 */
export function workspaceHomeLandingPath(workspaceId: string, landing: WorkspaceHomeLanding): string {
  if (landing === 'dashboard') return workspaceDashboardPath(workspaceId);
  if (landing === 'pages') return workspacePageListPath(workspaceId);
  return `/${workspaceId}`;
}

export function parseWorkspaceHomePath(pathname: string, workspaceId: string): ParsedWorkspaceHomePath | null {
  const id = workspaceId.trim();
  if (!id) return null;
  const prefix = `/${id}`;
  if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) return null;

  const rest = pathname.slice(prefix.length).replace(/\/+$/, '');
  if (rest === '') return { section: 'root' };

  const parts = rest.split('/').filter(Boolean).map((part) => {
    try {
      return decodeURIComponent(part);
    } catch {
      return part;
    }
  });

  if (parts[0] === 'dashboard' && parts.length === 1) return { section: 'dashboard' };
  if (parts[0] === 'pages' && parts.length === 1) return { section: 'pages' };
  if (parts[0] === 'threads' && (parts.length === 1 || parts.length === 2)) {
    return { section: 'threads', threadId: parts[1] ?? null };
  }
  return null;
}

/**
 * 旧 `?tab=page|dashboard|thread` をパスへ移す。
 * 残りのクエリ（create や embed など）は維持する。対象外なら null。
 */
export function legacyWorkspaceTabPath(workspaceId: string, search: string): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const tab = params.get('tab');
  if (tab !== 'page' && tab !== 'dashboard' && tab !== 'thread') return null;

  const threadId = params.get('threadId');
  params.delete('tab');
  params.delete('threadId');
  const query = params.toString();

  if (tab === 'dashboard') {
    return query ? `${workspaceDashboardPath(workspaceId)}?${query}` : workspaceDashboardPath(workspaceId);
  }
  if (tab === 'page') {
    return query ? `/${workspaceId}/pages?${query}` : `/${workspaceId}/pages`;
  }
  return workspaceThreadsPath(workspaceId, {
    threadId: threadId?.trim() || undefined,
    search: query,
  });
}

export function readWorkspaceTabFromLocation(
  pathname: string,
  search: string,
): 'dashboard' | 'pages' | 'threads' | null {
  const section = pathname.split('/').filter(Boolean)[1];
  if (section === 'dashboard' || section === 'pages' || section === 'threads') return section;
  const tab = new URLSearchParams(search).get('tab');
  if (tab === 'dashboard') return 'dashboard';
  if (tab === 'page') return 'pages';
  if (tab === 'thread') return 'threads';
  return null;
}

export function readThreadIdFromLocation(pathname: string, search: string): string | null {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[1] === 'threads' && parts[2]) {
    try {
      return decodeURIComponent(parts[2]);
    } catch {
      return parts[2];
    }
  }
  const params = new URLSearchParams(search);
  if (params.get('tab') === 'thread') {
    const threadId = params.get('threadId')?.trim();
    return threadId || null;
  }
  return null;
}
